import type { Node } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import type { EditorView, NodeView } from "prosemirror-view";

import { isFormulaNode } from "../adapter/schema";
import { openFormulaPopoverForm } from "../toolbar/formula";

export const FORMULA_ERROR_COLOR = "#E84121"; // Red

export class FormulaView implements NodeView {
  dom: HTMLSpanElement;

  private formula: string;

  constructor(node: Node, view: EditorView, getPos: () => number | undefined) {
    this.dom = document.createElement("span");
    this.dom.className = "formula rounded p-1";
    if (view.editable) {
      this.dom.classList.add("cursor-pointer", "hover:bg-blue-tint-90");
    }
    this.formula = isFormulaNode(node) ? node.attrs.formula : "";
    this.renderMath(this.formula);

    this.dom.addEventListener("click", () => {
      if (!view.editable) return;
      const pos = getPos();
      if (pos === undefined) return;

      view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));
      openFormulaPopoverForm(view, this.formula);
    });
  }

  private renderMath(latex: string): void {
    if (!window.katex) {
      this.dom.textContent = latex;
      return;
    }
    window.katex.render(latex, this.dom, {
      throwOnError: false,
      errorColor: FORMULA_ERROR_COLOR,
    });
  }

  update(node: Node): boolean {
    if (!isFormulaNode(node)) return false; // let ProseMirror handle the node view
    this.formula = node.attrs.formula;
    this.renderMath(this.formula);
    return true; // update applied; keep this node view
  }

  selectNode(): void {
    this.dom.classList.add("is-selected");
  }

  deselectNode(): void {
    this.dom.classList.remove("is-selected");
  }

  // eslint-disable-next-line class-methods-use-this
  ignoreMutation(): boolean {
    // KaTeX injects a complex DOM tree (nested spans, SVGs) inside this.dom.
    // Without this, ProseMirror would interpret those mutations as user edits.
    return true;
  }
}

/** Factory for FormulaView. */
export function formulaNodeView(
  node: Node,
  view: EditorView,
  getPos: () => number | undefined,
): FormulaView {
  return new FormulaView(node, view, getPos);
}
